import type { JSX, LazyExoticComponent } from "react";
import type { Permission } from "@mainsail/schema";
import type { RoutesFactory } from "../services/navigation/navigation-service";
import { Suspense, lazy } from "react";
import { redirect } from "react-router";
import { defaultLoader } from "./default-loader";
import { NotFound } from "./not-found";
import { Page } from "./page";
import { RouteErrorBoundary } from "./router-error-boundary";


type LazyRouteProps = {
	route: LazyExoticComponent<() => JSX.Element>;
};
const LazyRoute = ({ route: Route }: LazyRouteProps): JSX.Element => (
	<Suspense>
		<Route/>
	</Suspense>
);

const BrokerSearchPage = lazy(() => import("../pages/broker/search"));
const BrokerProposalsPage = lazy(() => import("../pages/broker/proposals"));
const BrokerProposalDetailsPage = lazy(() => import("../pages/broker/proposals/details"));
const CmsImagesPage = lazy(() => import("../pages/cms/images"));
const CrowsPage = lazy(() => import("../pages/admin/crows"));
const ChargebeeStatePage = lazy(() => import("../pages/admin/chargebee-state"));
const CrowDetailsPage = lazy(() => import("../pages/admin/crows/details"));
const EmailHistoryPage = lazy(() => import("../pages/history"));
const ItinerariesPage = lazy(() => import("../pages/broker/itinerary-templates"));
const ItineraryEditorPage = lazy(() => import("../pages/broker/itinerary-templates/builder"));
const ItineraryLocationsPage = lazy(() => import("../pages/itinerary-locations"));
const SignUpRequestsPage = lazy(() => import("../pages/admin/sign-up-requests"));
const SettingsPage = lazy(() => import("../pages/settings"));
const VideoManagementPage = lazy(() => import("../pages/admin/videos"));
const CentralOffersPage = lazy(() => import("../pages/central/offers"));
const CentralFleetPage = lazy(() => import("../pages/central/fleet"));
const ViewYachtCsvPage = lazy(() => import("../pages/admin/csv/viewyacht"));
const AlertsPage = lazy(() => import("../pages/admin/alerts"));
const AlertDetailsPage = lazy(() => import("../pages/admin/alerts/alert-details"));

export const makeRoutes: RoutesFactory = services => {
	const permissionGateLoader = (permission: Permission) => () => {
		if(!services.auth.hasPermission(permission)) {
			window.location.href = services.reference.setting("agentUrl");
		}

		return null;
	};

	const redirectLoader = (nextPath: string) => async () => Promise.resolve(redirect(nextPath));

	return [
		{
			path: "/",
			element: <Page/>,
			loader: defaultLoader(services),
			ErrorBoundary: RouteErrorBoundary,
			children: [

				{
					path: "/history",
					element: <LazyRoute route={ EmailHistoryPage }/>,
				},
				{
					path: "/itinerary-locations",
					loader: permissionGateLoader("ADMIN"),
					element: <LazyRoute route={ ItineraryLocationsPage }/>,
				},
				{
					path: "/broker",
					children: [
						{
							path: "/broker/search",
							element: <LazyRoute route={ BrokerSearchPage }/>,
						},
						{
							path: "/broker/proposals",
							element: <LazyRoute route={ BrokerProposalsPage }/>,
						},
						{
							path: "/broker/proposals/:id",
							element: <LazyRoute route={ BrokerProposalDetailsPage }/>,
						},
						{
							path: "/broker/itinerary-templates",
							loader: permissionGateLoader("ADMIN"),
							element: <LazyRoute route={ ItinerariesPage }/>,
						},
						{
							path: "/broker/itinerary-templates/:id",
							loader: permissionGateLoader("ADMIN"),
							element: <LazyRoute route={ ItineraryEditorPage }/>,
						},
					],
				},
				{
					path: "/central",
					children: [
						{
							path: "/central/offers",
							element: <LazyRoute route={ CentralOffersPage }/>,
						},
						{
							path: "/central/fleet/:id?",
							element: <LazyRoute route={ CentralFleetPage }/>,
						},
					],
				},
				{
					path: "/cms",
					loader: permissionGateLoader("ADMIN"),
					children: [
						{
							path: "/cms/images",
							element: <LazyRoute route={ CmsImagesPage }/>,
						},
					],
				},
				{
					path: "/admin",
					loader: permissionGateLoader("ADMIN"),
					children: [
						{
							path: "/admin/alerts",
							element: <LazyRoute route={ AlertsPage }/>,
						},
						{
							path: "/admin/alerts/:id",
							element: <LazyRoute route={ AlertDetailsPage }/>,
						},
						{
							path: "/admin/csv/viewyacht",
							element: <LazyRoute route={ ViewYachtCsvPage }/>,
						},
						{
							path: "/admin/chargebee-state/:companyId",
							element: <LazyRoute route={ ChargebeeStatePage }/>,
						},

						{
							path: "/admin/crows",
							element: <LazyRoute route={ CrowsPage }/>,
						},
						{
							path: "/admin/crows/:crowId",
							element: <LazyRoute route={ CrowDetailsPage }/>,
						},
						{
							path: "/admin/sign-up-requests",
							element: <LazyRoute route={ SignUpRequestsPage }/>,
						},
						{
							path: "/admin/videos",
							element: <LazyRoute route={ VideoManagementPage }/>,
						},
					],
				},
				{
					path: "/settings/:settingsTab?",
					element: <LazyRoute route={ SettingsPage }/>,
				},
				{
					path: "/*",
					element: <NotFound/>,
				},
			],
		},

		// Redirects for pages moved into /settings
		{
			path: "/fleet-distribution",
			loader: redirectLoader("/settings/fleet-distribution"),
		},
		{
			path: "/company",
			loader: redirectLoader("/settings/agency-and-users"),
		},
		{
			path: "/rys",
			loader: redirectLoader("/settings/yacht-search"),
		},
	];
};
